import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faPlay } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

interface CampaignPausedNoticeProps {
	loading: boolean;
	onResume: () => void;
}

/**
 * Says the lead's campaign is held, and offers to put it back in motion (PREC-3906).
 *
 * Pausing holds the lead's queued steps (`active = false`) and writes the per-person cut-off; both
 * are reversible, so the panel owes the user the way back. Without it a paused lead reads exactly
 * like a lead that was never in a campaign — and the Outbox would still file it under "Paused",
 * promising a resume nothing offered.
 *
 * Sits under the ordinary action row rather than replacing it: a paused lead can still be contacted
 * by hand, which is often why it was paused.
 */
export const CampaignPausedNotice: FunctionComponent<CampaignPausedNoticeProps> = ({
	loading,
	onResume,
}) => {
	const { t } = useTranslation();

	return (
		<div
			className="mt-1.5 flex items-center gap-2 rounded-[10px] border border-gray-200 bg-gray-50 px-2.5 py-2"
			data-testid="lead-panel-campaign-paused"
		>
			<span className="h-2 w-2 shrink-0 rounded-full bg-status-warning" />
			<div className="min-w-0 flex-1 truncate text-xs font-semibold text-text">
				{t("contacts.leadPanel.actions.campaign.paused.label")}
			</div>
			<button
				className={classNames(
					"flex h-7 shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-brand-light px-2.5 text-xs font-semibold text-brand-light duration-200",
					"hover:bg-brand-light hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
				)}
				data-testid="lead-panel-resume-campaign"
				disabled={loading}
				type="button"
				onClick={onResume}
			>
				<Icon className="w-3" icon={faPlay} />
				{t("contacts.leadPanel.actions.campaign.paused.resume")}
			</button>
		</div>
	);
};
